"use client";

import { useEffect, useState, memo } from "react";
import { montserratExtraBold, montserratMedium } from "../fonts";
import MagicBento from "./magic-bento";

type CountdownProps = {
  targetDate: string;
};

const CountdownTimer = memo(function CountdownTimer({ targetDate }: CountdownProps) {
  const [hasLaunched, setHasLaunched] = useState(false);
  const [segments, setSegments] = useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  useEffect(() => {
    const second = 1000;
    const minute = second * 60;
    const hour = minute * 60;
    const day = hour * 24;
    const target = new Date(targetDate).getTime();

    const updateCountdown = () => {
      const now = Date.now();
      const distance = target - now;
      if (distance <= 0) {
        setSegments({ days: "00", hours: "00", minutes: "00", seconds: "00" });
        setHasLaunched(true);
        return true;
      }

      setSegments({
        days: String(Math.floor(distance / day)).padStart(2, "0"),
        hours: String(Math.floor((distance % day) / hour)).padStart(2, "0"),
        minutes: String(Math.floor((distance % hour) / minute)).padStart(2, "0"),
        seconds: String(Math.floor((distance % minute) / second)).padStart(2, "0"),
      });
      setHasLaunched(false);
      return false;
    };

    const shouldStop = updateCountdown();
    if (shouldStop) return;

    const interval = setInterval(() => {
      if (updateCountdown()) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return (
    <div className="mx-auto mt-6 -w-4xl p-6 text-center">
      <MagicBento
        textAutoHide={true}
        enableStars={true}
        enableSpotlight={true}
        enableBorderGlow={true}
        enableTilt={true}
        enableMagnetism={true}
        clickEffect={true}
        spotlightRadius={300}
        particleCount={12}
        glowColor="11, 113, 255"
        layoutVariant="uniform"
        cardClassName="card card--border-glow group flex flex-col gap-4 rounded-3xl border border-[color:var(--field-border)] bg-transparent p-0 transition-all duration-300"
        cards={Object.entries(segments).map(([label, value]) => ({
          title: label.toUpperCase(),
          description: value,
          color: "transparent",
        }))}
        renderCard={(card) => (
          <div className="flex flex-col items-center gap-2 px-6 py-5">
            <span className={`${montserratExtraBold.className} text-xl md:text-5xl tracking-tight`}>
              {card.description}
            </span>
            <span className={`${montserratMedium.className} text-xs md:text-sm uppercase tracking-[0.4em] text-white/70`}>
              {card.title}
            </span>
          </div>
        )}
      />
    </div>
  );
});

export default CountdownTimer;

